import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, FileText, Cookie, ArrowLeft } from 'lucide-react';

const Legal = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const isPrivacy = pathname.includes('privacy');
  const isTerms = pathname.includes('terms');
  // const isCookie = pathname.includes('cookie'); (unused)

  const getContent = () => {
    if (isPrivacy) {
      return {
        title: 'Privacy Policy',
        icon: Shield,
        updated: 'June 15, 2024',
        sections: [
          { title: 'Information We Collect', content: 'We collect information you provide directly to us, such as when you create an account, subscribe to our newsletter, or contact us for support. This may include your name, email address, and phone number.' },
          { title: 'How We Use Your Information', content: 'We use the information we collect to provide, maintain, and improve our services, to communicate with you, and to protect our users and our services.' },
          { title: 'Information Sharing', content: 'We do not share your personal information with third parties except as described in this policy, such as with your consent or to comply with legal obligations.' }
        ]
      };
    }
    if (isTerms) {
      return {
        title: 'Terms of Service',
        icon: FileText,
        updated: 'June 15, 2024',
        sections: [
          { title: 'Acceptance of Terms', content: 'By accessing or using our services, you agree to be bound by these terms of service and all applicable laws and regulations.' },
          { title: 'Use of Services', content: 'You may use our services only for lawful purposes and in accordance with these terms. You are responsible for all activity that occurs under your account.' },
          { title: 'Intellectual Property', content: 'All content and materials available through our services are the property of OETechs or its licensors and are protected by intellectual property laws.' }
        ]
      };
    }
    return {
      title: 'Cookie Policy',
      icon: Cookie,
      updated: 'June 15, 2024',
      sections: [
        { title: 'What are Cookies?', content: 'Cookies are small text files that are stored on your device when you visit a website. They help the website remember your preferences and improve your experience.' },
        { title: 'How We Use Cookies', content: 'We use cookies to understand how you use our website, to remember your settings, and to provide personalized content and advertisements.' },
        { title: 'Your Choices', content: 'You can control or delete cookies through your browser settings. However, please note that disabling cookies may affect the functionality of our website.' }
      ]
    };
  };

  const data = getContent();

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] min-h-screen py-24 transition-colors duration-500">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 mb-12 transition-colors group">
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-[#001a35] rounded-[3rem] p-8 md:p-16 shadow-sm border border-slate-100 dark:border-white/10"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-16">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center">
                <data.icon size={32} />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-[#4F46E5] dark:text-white">{data.title}</h1>
                <p className="text-slate-400 text-sm mt-1 uppercase tracking-widest font-semibold">Last Updated: {data.updated}</p>
              </div>
            </div>
          </div>

          <div className="space-y-12">
            {data.sections.map((section, i) => (
              <div key={i}>
                <h3 className="text-xl font-semibold text-[#4F46E5] dark:text-white mb-4">{section.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-20 pt-12 border-t border-slate-100 dark:border-white/5">
            <p className="text-slate-400 text-sm text-center">
              If you have any questions about our {data.title}, please contact us at <a href="mailto:legal@oetechs.com" className="text-blue-600 font-semibold">legal@oetechs.com</a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Legal;
